import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { LuAlertCircle } from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditMaterial,
  getIsMaterialModalOpen,
  toggleMaterialModal,
} from "../../features/materials/materialsSlice";
import { IMaterialFormInput } from "../../types/material";
import { FileTextIcon, TagIcon } from "../icons";
import ItemPhoto from "../itemPhoto";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Textarea from "../ui/Textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "../ui/Dialog";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileFormClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
  fieldTextareaClass,
  formFieldIconClass,
} from "../ui/styles";

type Props = {
  handleSaveMaterial: (data: IMaterialFormInput) => Promise<boolean>;
};

const MaterialFormModal = ({ handleSaveMaterial }: Props) => {
  const dispatch = useAppDispatch();
  const editMaterial = useAppSelector(getEditMaterial);
  const isMaterialModalOpen = useAppSelector(getIsMaterialModalOpen);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<IMaterialFormInput>({
    defaultValues: {
      name: "",
      description: "",
      image: null,
    },
  });

  const handleOnSubmit: SubmitHandler<IMaterialFormInput> = async (data) => {
    setIsLoading(true);
    const saved = await handleSaveMaterial(data);
    setIsLoading(false);

    if (saved) {
      handleCloseFormModal();
    }
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleMaterialModal(false));
  };

  useEffect(() => {
    if (editMaterial && editMaterial.id) {
      reset({
        name: editMaterial.name,
        description: editMaterial.description || "",
        // A saved photo goes back only while its file still exists.
        image: editMaterial.image_url ? editMaterial.image : null,
      });
    } else {
      reset({
        name: "",
        description: "",
        image: null,
      });
    }
  }, [editMaterial, isMaterialModalOpen, reset]);

  return (
    <Dialog open={isMaterialModalOpen === true} onClose={handleCloseFormModal}>
      <DialogContent
        size="lg"
        className={cx(
          "font-inter max-h-[90vh] overflow-y-auto",
          dialogMobileContentClass
        )}
      >
        <DialogHeader className={dialogMobileHeaderClass}>
          <h2 className={dialogTitleClass}>મટીરીયલ ની માહિતી</h2>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(handleOnSubmit)}
          className={cx("space-y-4", dialogMobileFormClass)}
        >
          <div className={cx("space-y-4", dialogMobileBodyClass)}>
            <div className="space-y-2">
              <label className={fieldLabelClass}>
                નામ <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.name && "error")}>
                <TagIcon className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="મટીરીયલનું નામ દાખલ કરો"
                  className={fieldInputClass(!!errors.name)}
                  {...register("name", { required: true })}
                />
                {errors.name && <LuAlertCircle className={fieldErrorIconClass} />}
              </div>
              {errors.name && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  મટીરીયલનું નામ દાખલ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>ડિસ્ક્રિપ્શન (વૈકલ્પિક)</label>
              <div className="relative">
                <FileTextIcon className={formFieldIconClass} />
                <Textarea
                  placeholder="મટીરીયલનું ડિસ્ક્રિપ્શન દાખલ કરો"
                  className={fieldTextareaClass()}
                  {...register("description", { required: false })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>ફોટો (વૈકલ્પિક)</label>
              <Controller
                control={control}
                name="image"
                render={({ field }) => (
                  <ItemPhoto
                    value={field.value}
                    existingUrl={editMaterial?.image_url}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>

          <DialogFooter className={dialogMobileFooterClass}>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              રદ કરો
            </Button>

            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <span className={buttonSpinnerClass}></span>
                  સાચવી રહ્યા છીએ...
                </span>
              ) : (
                "સાચવો"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MaterialFormModal;
